import { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Button,
    Alert,
    AlertColor,
} from "@mui/material";
import { useAuth, useSnackbar, useLoading } from "../../Context/Contexts";
import { Payment } from "../../Types/SalesDocument/PaymentType";
import { useTranslation } from "react-i18next";
interface PayByLinkModalProps {
    open: boolean;
    onClose: () => void;
    xMobileNo: string;
    documentNo: string;
    initialPayments?: Payment[] | null;
    setPayByLink: (open : boolean) => void;
}

interface PayByLinkInitResponse {
    sessionId: string
}

interface PayByLinkStatusResponse {
    sessionStatus: "INITIALIZED" | "WAITING" | "SUCCESS" | "DENIED" | "COMPLETED" | string;
}

interface StatusMessage {
    text: string;
    severity: AlertColor;
}

const PayByLinkModal: React.FC<PayByLinkModalProps & { setPaymentsOnDocument: React.Dispatch<React.SetStateAction<Payment[]>> }> = ({ open, onClose, xMobileNo, documentNo, initialPayments, setPaymentsOnDocument, setPayByLink }) => {
    const { authService } = useAuth();
    const { showLoading, hideLoading } = useLoading();
    const { showSnackbar } = useSnackbar();
    const [payments, setPayments] = useState<Payment[]>(initialPayments ?? []);
    const [mobileNumber, setMobileNumber] = useState<string>(xMobileNo);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [status, setStatus] = useState<StatusMessage | null>(null);
    const { t } = useTranslation();
    useEffect(() => {
        if (isLoading) showLoading();
        else hideLoading();
    }, [isLoading, showLoading, hideLoading]);

    const initiatePayment = async () => {

        if (!mobileNumber.trim()) {
            setStatus({ text: t("dialog.mobileRequired"), severity: "error" });
            return;
        }
        setIsLoading(true);
        setStatus({ text: "", severity: "info" });

        try {
            const response = await authService.post<PayByLinkInitResponse>("/orderApi/PayByLink", {
                mobileNumber, documentNo
            });

            if (!response) throw new Error("Failed to initiate payment");

            await pollStatus(response.sessionId);
        } catch (error)
        {
            console.error("Error initiating payment:", error);
         
            setStatus({ text: t("dialog.paymentInitError"), severity: "error" });
        } finally {
            setIsLoading(false);
        }
    };

    const pollStatus = async (sessionId: string) => {
        const interval = 5000;
        let attempts = 100;

        while (attempts > 0) {
            try {
                const response = await authService.post<PayByLinkStatusResponse>("/orderApi/PayByLinkStatus", { paymentSessionId : sessionId, documentNo });

                if (!response) throw new Error("Failed to fetch status");
              
                if (response.sessionStatus === "SUCCESS" || response.sessionStatus ==="COMPLETED") {
                    setStatus({ text: t("dialog.paymentSuccess"), severity: "success" });
                    showSnackbar(t("dialog.paymentSuccess"), "success");

                    //need to push payment into current
                    await fetchPayment(sessionId);
                    setTimeout(() => {
                        setPayByLink(false);
                    }, 5000);
                    return;
                }
                else if (response.sessionStatus === "DENIED") {
                    setStatus({ text: t("dialog.paymentDenied"), severity: "error" });
                    showSnackbar(t("dialog.paymentDenied"), "warning");
                    return;
                }
                //dont return to keep polling
                else if (response.sessionStatus === "WAITING") {
                    setStatus({ text: t("dialog.awaitingConfirmation"), severity: "info" });
          
                }
                else if (response.sessionStatus === "INITIALIZED") {
                    setStatus({ text: t("dialog.paymentInitialized"), severity: "info" });

                }
                
            } catch (error) {
                console.error("Error fetching status:", error);
                setStatus({ text: t("dialog.statusFetchError"), severity: "error" });
                showSnackbar(t("dialog.statusFetchError"), "error");
                return;
            }

            attempts--;
            await new Promise((resolve) => setTimeout(resolve, interval));
        }


        setStatus({ text: t("dialog.statusUnknown"), severity: "info" });
    };


    const fetchPayment = async (sessionId: string) => {
        try {
            const response = await authService.post<Payment>("/orderApi/GetPaymentByPSPReference", { paymentSessionId: sessionId, documentNo });

            if (!response) throw new Error("Failed to fetch payment");
            
            setPayments((prev) => {
                const updatedPayments = [response, ...prev]
         
                setPaymentsOnDocument(updatedPayments); // Ensure latest state is passed
                return updatedPayments;
            });

        } catch (error:any) {
            console.error("Error fetching status:", error);
            showSnackbar(`${t("dialog.paymentFetchError")} : ${error.response?.data ?? error.message}`, "error");

            return;
        }
    };

    return (
        <Dialog open={open} onClose={onClose}>
            <DialogTitle>Initiate Payment</DialogTitle>
            <DialogContent>
                {!mobileNumber.trim() && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        {t("dialog.mobileRequired")}
                    </Alert>
                )}
                <TextField
                    label={t("common.mobileNumber")}
                    fullWidth
                    value={mobileNumber}
                    onChange={(e) => setMobileNumber(e.target.value)}
                    margin="dense"
                />
                {status && <Alert severity={status.severity} sx={{ mt: 2 }}>{status.text}</Alert>}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isLoading}>
                    {t("common.cancel")}
                </Button>
                <Button onClick={initiatePayment} disabled={isLoading || !mobileNumber.trim()} variant="contained" color="primary">
                    {t("common.initiate")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PayByLinkModal;
